import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import startConversationMutation from '../graphql/mutations/startConversation.mutation';
import { ConversationView } from './PrivateMessages';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrentUserProfile } from '../utils';
import { useGuestAuth } from '../context/GuestAuthContext';
import VerifyEmailDialog from './VerifyEmailDialog';
import './ContactSellerButton.css';

interface ContactSellerButtonProps {
  listingId: number;
  sellerId: number;
  currentUserId: number;
  darkMode?: boolean;
  listingAvailable?: boolean;
  listingInfo?: {
    make: string;
    model: string;
    year: number;
    trim?: string;
    price: string;
    imageLoc: any;
  };
}

const ContactSellerButton: React.FC<ContactSellerButtonProps> = ({
  listingId,
  sellerId,
  currentUserId,
  darkMode = false,
  listingAvailable = true,
  listingInfo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversation, setConversation] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { emailVerified, profile, refetch, loading: profileLoading } = useCurrentUserProfile();
  const { openSignIn } = useGuestAuth();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;

  const [startConversation, { loading: starting }] = useMutation(startConversationMutation, {
    onCompleted: (data) => {
      const conv = data?.startConversation?.conversation;
      if (!conv) {
        setErrorMessage('Could not open the conversation. Please try again.');
        return;
      }
      setConversation(conv);
      setErrorMessage(null);
      setIsOpen(true);
    },
    onError: (error) => {
      const msg = error.message || 'Failed to start conversation.';
      if (/email/i.test(msg) && /verif/i.test(msg)) {
        setVerifyOpen(true);
        return;
      }
      setErrorMessage(msg);
    },
  });

  const handleOpenChat = async () => {
    setErrorMessage(null);
    if (emailVerified === false) {
      setVerifyOpen(true);
      return;
    }
    await startConversation({ variables: { listingId } });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (currentUserId === sellerId) {
    return null;
  }

  if (!listingAvailable) {
    return (
      <div className={`contact-seller-wrapper ${darkMode ? 'dark' : ''}`}>
        <p className="contact-seller-unavailable" data-testid="contact-seller-unavailable">
          Messaging is unavailable for this listing.
        </p>
      </div>
    );
  }

  if (!currentUserId) {
    return (
      <div className={`contact-seller-wrapper ${darkMode ? 'dark' : ''}`}>
        <button
          type="button"
          className="contact-seller-login-prompt"
          data-testid="contact-seller-login"
          onClick={openSignIn}
        >
          <ChatIcon />
          <span>Log in to contact seller</span>
        </button>
      </div>
    );
  }

  const isUnverified = emailVerified === false;
  const waitingOnProfile = profileLoading && emailVerified === null;

  return (
    <div className={`contact-seller-wrapper ${darkMode ? 'dark' : ''}`}>
      <button
        className="contact-seller-btn"
        data-testid="contact-seller"
        onClick={handleOpenChat}
        disabled={waitingOnProfile || starting}
        aria-busy={starting || waitingOnProfile}
      >
        <ChatIcon />
        <span>{starting ? 'Opening chat…' : 'Contact Seller'}</span>
      </button>
      {isUnverified && (
        <p id="contact-seller-verify-hint" className="contact-seller-verify-hint">
          Verify your email to message sellers.
        </p>
      )}
      {errorMessage && (
        <p className="contact-seller-error" role="alert" data-testid="contact-seller-error">
          {errorMessage}
        </p>
      )}
      {token && (
        <VerifyEmailDialog
          open={verifyOpen}
          token={token}
          email={profile?.email}
          darkMode={darkMode}
          onVerified={() => {
            setVerifyOpen(false);
            refetch().then(() => {
              startConversation({ variables: { listingId } });
            });
          }}
          onClose={() => setVerifyOpen(false)}
        />
      )}

      {isOpen && conversation && (
        <div
          className="chat-modal-overlay"
          onClick={handleClose}
          role="presentation"
        >
          <div
            className={`chat-modal ${darkMode ? 'dark' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Message seller"
            data-testid="chat-modal"
          >
            <button
              type="button"
              className="chat-modal-close"
              onClick={handleClose}
              aria-label="Close chat"
            >
              <CloseIcon />
            </button>
            <ConversationView
              conversation={{
                ...conversation,
                carsForSaleByListingId: conversation.carsForSaleByListingId || {
                  id: listingId,
                  ...listingInfo,
                },
              }}
              currentUserId={currentUserId}
              darkMode={darkMode}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSellerButton;
